import { useEffect, useRef, useState } from 'react'; // useRef kept for StickyHeader's lastY
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useCart, useUpdateCartItem, useRemoveCartItem, useUpdateCartBundle, useRemoveCartBundle } from '../hooks/useCart';
import { useWishlist } from '../context/WishlistContext';
import {
  useSettings, useMenu, useSocialLinks, usePaymentMethods, usePublicCategories,
} from '../hooks/usePublicContent';
import { useCurrency } from '../hooks/useCurrency';
import { Container } from './ui/Container';
import { SearchOverlay } from './SearchOverlay';
import { EditorialImage } from './ui/EditorialImage';
import type {
  HeaderSettings, FooterSettings, FooterWidget,
  HeaderLayout, StickyBehavior, BrandSettings, LogoComponent, BrandLogos, MenuItem,
} from '../types';

const API_URL = import.meta.env.VITE_API_URL as string;

// ─── Defaults ──────────────────────────────────────────────────────────────────

const DEFAULT_LOGO_COMPONENT: LogoComponent = {
  source: 'brand', custom_logo: null, max_width: 180, alignment: 'left',
  border_radius: 0, object_fit: 'contain', maintain_aspect_ratio: true,
  responsive: {
    desktop: { width: null, height: null, max_width: 180 },
    tablet:  { width: null, height: null, max_width: 160 },
    mobile:  { width: null, height: null, max_width: 140 },
  },
  state_sizes: { default: 180, sticky: 140, transparent: 220, scrolled: 140, mobile_menu: 140 },
  state_variants: { default: 'auto', sticky: 'auto', transparent: 'auto', mobile_menu: 'auto', dark: 'auto', light: 'auto' },
  position: 'left', show_brand_name: false, show_tagline: false,
  retina_support: true, lazy_load: false, hide_desktop: false, hide_tablet: false, hide_mobile: false,
  click_action: 'home',
};

const DEFAULT_HEADER: HeaderSettings = {
  layout: 'classic',
  sticky: { enabled: true, behavior: 'always' },
  top_bar: { enabled: false, content: 'Welcome to our store!' },
  promo_bar: { enabled: true, text: 'Free shipping on orders over $75 · 30-day returns · Ships in 3–5 days' },
  actions: { search: true, wishlist: true, cart: true, account: false },
  border: true,
  colors: { bg: '', text: '', link: '', link_hover: '', border: '', icon: '', icon_hover: '', top_bar_bg: '', top_bar_text: '', promo_bar_bg: '', promo_bar_text: '' },
  logo_component: DEFAULT_LOGO_COMPONENT,
};

const DEFAULT_FOOTER: FooterSettings = {
  template: 'default',
  columns: [
    { width: 35, widgets: [{ type: 'logo' }, { type: 'text', content: 'Premium art prints in solid-wood frames. Archival ink, museum-grade materials, delivered to your door.' }, { type: 'social_icons' }, { type: 'payment_icons' }] },
    { width: 22, widgets: [{ type: 'nav_links', title: 'Shop', links: [{ label: 'All Prints', url: '/categories/wall-art' }, { label: 'Best Sellers', url: '/categories/wall-art' }, { label: 'Custom Prints', url: '/custom-prints' }] }] },
    { width: 22, widgets: [{ type: 'nav_links', title: 'Info', links: [{ label: 'About Us', url: '/about' }, { label: 'FAQs', url: '/faqs' }, { label: 'Contact', url: '/contact' }] }] },
    { width: 21, widgets: [{ type: 'newsletter', eyebrow: 'Get 10% off', heading: 'Join our list for first access to new drops and offers.', placeholder: 'Your email', button_text: 'Join →' }] },
  ],
  trust_bar: { enabled: true, items: [{ icon: 'truck', title: 'Free Shipping', subtitle: 'On orders over $75' }, { icon: 'shield', title: 'Secure Checkout', subtitle: '256-bit SSL encrypted' }, { icon: 'refresh', title: '30-Day Returns', subtitle: 'No questions asked' }, { icon: 'star', title: '7-Year Guarantee', subtitle: 'Against warping or fading' }] },
  social_icons: { size: 'md', shape: 'none' },
  copyright: { suffix: 'All rights reserved.' },
  colors: { bg: '', secondary_bg: '', text: '', secondary_text: '', link: '', link_hover: '', heading: '', border: '', icon: '', icon_hover: '', newsletter_bg: '', newsletter_button: '', copyright_bg: '', copyright_text: '' },
  logo_component: DEFAULT_LOGO_COMPONENT,
};

const FALLBACK_NAV = [
  { id: 0, label: 'Shop All', url: '/categories/wall-art', children: [] },
  { id: 1, label: 'Best Sellers', url: '/categories/wall-art', children: [] },
  { id: 2, label: 'Custom Prints', url: '/custom-prints', children: [] },
];

const DEFAULT_BRAND: BrandSettings = {
  logos: { primary: null, dark: null, light: null, mobile: null, sticky: null, transparent: null, retina: null, svg: null, favicon: null, apple_touch_icon: null, pwa_icon: null },
  identity: { brand_name: '', tagline: '', company_name: '', copyright_name: '', website_url: '' },
  colors: { primary: '#1a1a1a', secondary: '#c9a96e', accent: '#e8ddd0', success: '#22c55e', warning: '#f59e0b', danger: '#ef4444', background: '#f5f0e8', surface: '#ffffff', text: '#1a1a1a', muted_text: '#6b7280', border: '#e5e7eb' },
  typography: { heading_font: 'Playfair Display', body_font: 'Inter', button_font: '', font_scale: 'base' },
};

type LogoState = 'default' | 'sticky' | 'transparent' | 'mobile_menu';

function resolveLogoUrl(
  brandLogos: BrandLogos,
  logoCfg: LogoComponent,
  fallbackLogo: string | null,
  state: LogoState,
): string | null {
  if (logoCfg.source === 'custom') return logoCfg.custom_logo || fallbackLogo;

  const variant = (logoCfg.state_variants[state as keyof typeof logoCfg.state_variants] ?? 'auto');

  if (variant === 'auto') {
    if (state === 'sticky')      return brandLogos.sticky || brandLogos.dark || brandLogos.primary || fallbackLogo;
    if (state === 'transparent') return brandLogos.transparent || brandLogos.light || brandLogos.primary || fallbackLogo;
    if (state === 'mobile_menu') return brandLogos.mobile || brandLogos.primary || fallbackLogo;
    return brandLogos.primary || fallbackLogo;
  }

  return (brandLogos[variant as keyof BrandLogos] as string | null) || brandLogos.primary || fallbackLogo;
}

function resolveLogoWidth(logoCfg: LogoComponent, state: LogoState): number | null {
  return (logoCfg.state_sizes[state as keyof typeof logoCfg.state_sizes] ?? null);
}

// ─── Trust icons ───────────────────────────────────────────────────────────────

function TrustIcon({ name, cls }: { name: string; cls?: string }) {
  const c = cls ?? 'mx-auto mb-3 h-6 w-6 opacity-60';
  switch (name) {
    case 'truck':   return <svg className={c} fill="none" stroke="currentColor" strokeWidth="1.6" viewBox="0 0 24 24"><path d="M1 3h15v13H1z"/><path d="M16 8h4l3 4v4h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>;
    case 'shield':  return <svg className={c} fill="none" stroke="currentColor" strokeWidth="1.6" viewBox="0 0 24 24"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>;
    case 'refresh': return <svg className={c} fill="none" stroke="currentColor" strokeWidth="1.6" viewBox="0 0 24 24"><path d="M1 4v6h6"/><path d="M23 20v-6h-6"/><path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4-4.64 4.36A9 9 0 0 1 3.51 15"/></svg>;
    case 'star':    return <svg className={c} fill="currentColor" viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>;
    case 'package': return <svg className={c} fill="none" stroke="currentColor" strokeWidth="1.6" viewBox="0 0 24 24"><path d="m12 3-9 4.5 9 4.5 9-4.5z"/><path d="M3 7.5v9L12 21l9-4.5v-9"/><path d="M12 12v9"/></svg>;
    case 'heart':   return <svg className={c} fill="currentColor" viewBox="0 0 24 24"><path d="M12 21s-7.5-4.6-10-9.2C.5 8.2 2.3 4 6.3 4c2 0 3.6 1 5.7 3.4C14.1 5 15.7 4 17.7 4c4 0 5.8 4.2 4.3 7.8C19.5 16.4 12 21 12 21z"/></svg>;
    case 'clock':   return <svg className={c} fill="none" stroke="currentColor" strokeWidth="1.6" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>;
    case 'check':   return <svg className={c} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M20 6 9 17l-5-5"/></svg>;
    default:        return null;
  }
}

// ─── Social icon SVGs ─────────────────────────────────────────────────────────

function SocialIconSvg({ platform, size }: { platform: string; size?: string }) {
  const px = size === 'lg' ? 22 : size === 'sm' ? 16 : 19;
  const p = platform.toLowerCase();
  if (p === 'instagram') return <svg width={px} height={px} fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>;
  if (p === 'facebook') return <svg width={px} height={px} fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>;
  if (p === 'twitter' || p === 'x') return <svg width={px} height={px} fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>;
  if (p === 'tiktok') return <svg width={px} height={px} fill="currentColor" viewBox="0 0 24 24"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V9.15a8.17 8.17 0 0 0 4.78 1.52V7.22a4.85 4.85 0 0 1-1.01-.53z"/></svg>;
  if (p === 'pinterest') return <svg width={px} height={px} fill="currentColor" viewBox="0 0 24 24"><path d="M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 0 1 .083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/></svg>;
  if (p === 'youtube') return <svg width={px} height={px} fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>;
  // generic
  return <svg width={px} height={px} fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 0 1 0 20M12 2a15.3 15.3 0 0 0 0 20"/></svg>;
}

// ─── Layout root ───────────────────────────────────────────────────────────────

export function Layout() {
  const { pathname } = useLocation();
  const [searchOpen, setSearchOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const { data: settings } = useSettings();

  const hdr: HeaderSettings = {
    ...DEFAULT_HEADER,
    ...(settings?.header ?? {}),
    sticky: { ...DEFAULT_HEADER.sticky, ...(settings?.header?.sticky ?? {}) },
    top_bar: { ...DEFAULT_HEADER.top_bar, ...(settings?.header?.top_bar ?? {}) },
    promo_bar: { ...DEFAULT_HEADER.promo_bar, ...(settings?.header?.promo_bar ?? {}) },
    actions: { ...DEFAULT_HEADER.actions, ...(settings?.header?.actions ?? {}) },
    colors: { ...DEFAULT_HEADER.colors, ...(settings?.header?.colors ?? {}) },
    logo_component: { ...DEFAULT_LOGO_COMPONENT, ...(settings?.header?.logo_component ?? {}) },
  };

  const brand: BrandSettings = {
    ...DEFAULT_BRAND,
    ...(settings?.brand ?? {}),
    logos:    { ...DEFAULT_BRAND.logos,    ...(settings?.brand?.logos    ?? {}) },
    identity: { ...DEFAULT_BRAND.identity, ...(settings?.brand?.identity ?? {}) },
    colors:   { ...DEFAULT_BRAND.colors,   ...(settings?.brand?.colors   ?? {}) },
  };

  const ftr: FooterSettings = {
    ...DEFAULT_FOOTER,
    ...(settings?.footer ?? {}),
    trust_bar: { ...DEFAULT_FOOTER.trust_bar, ...(settings?.footer?.trust_bar ?? {}), items: settings?.footer?.trust_bar?.items ?? DEFAULT_FOOTER.trust_bar.items },
    social_icons: { ...DEFAULT_FOOTER.social_icons, ...(settings?.footer?.social_icons ?? {}) },
    copyright: { ...DEFAULT_FOOTER.copyright, ...(settings?.footer?.copyright ?? {}) },
    columns: settings?.footer?.columns ?? DEFAULT_FOOTER.columns,
    colors: { ...DEFAULT_FOOTER.colors, ...(settings?.footer?.colors ?? {}) },
  };

  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);

  return (
    <div className="flex min-h-screen flex-col">
      {hdr.top_bar.enabled && <TopBar cfg={hdr.top_bar} colors={hdr.colors} />}
      {hdr.promo_bar.enabled && <PromoBar text={hdr.promo_bar.text} colors={hdr.colors} />}
      <StickyHeader hdr={hdr} brand={brand} settings={settings} onSearchClick={() => setSearchOpen(true)} onCartClick={() => setCartOpen(true)} />
      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />
      <main className="flex-1 bg-cream text-ink">
        <Outlet />
      </main>
      <Footer ftr={ftr} brand={brand} settings={settings} />
    </div>
  );
}

// ─── Top bar ───────────────────────────────────────────────────────────────────

function TopBar({ cfg, colors }: { cfg: HeaderSettings['top_bar']; colors: HeaderSettings['colors'] }) {
  return (
    <div style={{ background: colors.top_bar_bg || '#f5f0e8', color: colors.top_bar_text || '#1a1a1a' }}
      className="py-1.5 text-center text-[11px] font-medium"
      dangerouslySetInnerHTML={{ __html: cfg.content }} />
  );
}

// ─── Promo bar ─────────────────────────────────────────────────────────────────

function PromoBar({ text, colors }: { text: string; colors: HeaderSettings['colors'] }) {
  return (
    <div style={{ background: colors.promo_bar_bg || '#1a1a1a', color: colors.promo_bar_text || '#f5f0e8' }}
      className="py-2 text-center text-[11px] font-medium uppercase tracking-[0.1em]">
      {text}
    </div>
  );
}

// ─── Sticky header wrapper ─────────────────────────────────────────────────────

function StickyHeader({ hdr, brand, settings, onSearchClick, onCartClick }: {
  hdr: HeaderSettings;
  brand: BrandSettings;
  settings: ReturnType<typeof useSettings>['data'];
  onSearchClick: () => void;
  onCartClick: () => void;
}) {
  const [isSticky, setIsSticky] = useState(false);
  const [visible, setVisible] = useState(true);
  const lastY = useRef(0);
  const behavior: StickyBehavior = hdr.sticky.behavior ?? 'always';

  useEffect(() => {
    if (!hdr.sticky.enabled) return;
    if (behavior === 'always') return;

    const onScroll = () => {
      const y = window.scrollY;
      setIsSticky(y > 10);
      if (behavior === 'hide-on-scroll') {
        setVisible(y <= 60 || y < lastY.current);
      } else {
        setVisible(y > 80);
      }
      lastY.current = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [hdr.sticky.enabled, behavior]);

  const stickyClass = hdr.sticky.enabled
    ? behavior === 'show-on-scroll'
      ? `fixed top-0 left-0 right-0 transition-transform duration-300 ${visible ? 'translate-y-0' : '-translate-y-full'}`
      : behavior === 'hide-on-scroll'
        ? `sticky top-0 transition-transform duration-300 ${visible ? 'translate-y-0' : '-translate-y-full'}`
        : 'sticky top-0'
    : '';

  return (
    <div className={`z-40 ${stickyClass}`}>
      <Header hdr={hdr} brand={brand} settings={settings} onSearchClick={onSearchClick} onCartClick={onCartClick}
        layout={hdr.layout ?? 'classic'} isSticky={isSticky && hdr.sticky.enabled} />
    </div>
  );
}

// ─── Header ────────────────────────────────────────────────────────────────────

function Header({ hdr, brand, settings, onSearchClick, onCartClick, layout, isSticky = false }: {
  hdr: HeaderSettings;
  brand: BrandSettings;
  settings: ReturnType<typeof useSettings>['data'];
  onSearchClick: () => void;
  onCartClick: () => void;
  layout: HeaderLayout;
  isSticky?: boolean;
}) {
  const { ids: wishlistIds } = useWishlist();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { data: headerMenu } = useMenu('header');

  const shopAllCfg = settings?.shop_all;
  const shopSlug = shopAllCfg?.slug ?? '/shop';
  const shopLabel = shopAllCfg?.button_text ?? 'Shop All';

  const baseNavItems = headerMenu?.items?.length ? headerMenu.items : FALLBACK_NAV;
  const alreadyHasShop = baseNavItems.some(i => i.url === shopSlug);
  const navItems = (shopAllCfg?.enabled && shopAllCfg?.show_in_header && !alreadyHasShop)
    ? [{ id: -99, label: shopLabel, url: shopSlug, children: [] }, ...baseNavItems]
    : baseNavItems;

  const siteName = brand.identity.brand_name || settings?.site_name || 'Framed';
  const c = hdr.colors;
  const logoCfg = hdr.logo_component ?? DEFAULT_LOGO_COMPONENT;

  const bgStyle = c.bg ? { backgroundColor: c.bg } : undefined;
  const textStyle = c.text ? { color: c.text } : undefined;
  const baseCls = c.bg ? '' : 'bg-cream/95 backdrop-blur-md text-ink';
  const borderCls = hdr.border ? (c.border ? `border-b` : 'border-b border-stone-light') : '';
  const borderStyle = c.border ? { borderColor: c.border } : undefined;

  // Determine current logo state for variant/size resolution
  const logoState: LogoState = mobileOpen ? 'mobile_menu' : isSticky ? 'sticky' : 'default';
  const resolvedLogoUrl = resolveLogoUrl(brand.logos, logoCfg, settings?.logo ?? null, logoState);
  const resolvedWidth = resolveLogoWidth(logoCfg, logoState) ?? logoCfg.max_width ?? 180;


  const logoImgStyle: React.CSSProperties = {
    maxWidth: resolvedWidth,
    objectFit: logoCfg.object_fit,
    borderRadius: logoCfg.border_radius || undefined,
    transition: 'max-width 0.3s ease',
  };

  const Logo = logoCfg.click_action === 'none' ? (
    <div className={`shrink-0 flex items-center ${logoCfg.hide_desktop ? 'lg:hidden' : ''} ${logoCfg.hide_mobile ? 'max-lg:hidden' : ''}`}>
      {renderLogoContent()}
    </div>
  ) : (
    <Link to="/" aria-label={siteName} className={`shrink-0 flex items-center ${logoCfg.hide_desktop ? 'lg:hidden' : ''} ${logoCfg.hide_mobile ? 'max-lg:hidden' : ''}`}>
      {renderLogoContent()}
    </Link>
  );

  function renderLogoContent() {
    if (resolvedLogoUrl) {
      return (
        <>
          <img src={`${API_URL}${resolvedLogoUrl}`} alt={siteName} style={logoImgStyle}
            loading={logoCfg.lazy_load ? 'lazy' : 'eager'}
            className="h-auto w-auto object-contain" />
          {logoCfg.show_brand_name && (
            <span className="ml-3 font-serif text-xl tracking-tight">{siteName}</span>
          )}
        </>
      );
    }
    // Text fallback
    return (
      <div>
        <span className="font-serif text-2xl tracking-tight">
          {siteName.replace(/\.$/, '')}<span className="text-clay">.</span>
        </span>
        {logoCfg.show_tagline && brand.identity.tagline && (
          <p className="text-[10px] tracking-widest opacity-60 mt-0.5">{brand.identity.tagline}</p>
        )}
      </div>
    );
  }

  const NavLinks = (
    <nav className="hidden items-center gap-7 lg:flex">
      {navItems.map(item => (
        <NavItem key={item.id} item={item} colors={c} />
      ))}
    </nav>
  );

  const Actions = (
    <div className="flex items-center gap-4 shrink-0">
      {hdr.actions.search && (
        <button type="button" onClick={onSearchClick} aria-label="Search"
          style={c.icon ? { color: c.icon } : undefined}
          className="text-ink-soft hover:text-ink transition-colors">
          <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/>
          </svg>
        </button>
      )}
      {hdr.actions.wishlist && (
        <Link to="/wishlist" aria-label="Wishlist"
          style={c.icon ? { color: c.icon } : undefined}
          className="relative text-ink-soft hover:text-ink transition-colors">
          <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <path d="M12 21s-7.5-4.6-10-9.2C.5 8.2 2.3 4 6.3 4c2 0 3.6 1 5.7 3.4C14.1 5 15.7 4 17.7 4c4 0 5.8 4.2 4.3 7.8C19.5 16.4 12 21 12 21z"/>
          </svg>
          {wishlistIds.length > 0 && (
            <span className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center bg-clay text-[9px] font-semibold text-cream">
              {wishlistIds.length}
            </span>
          )}
        </Link>
      )}
      {hdr.actions.cart && (
        <button type="button" onClick={onCartClick} aria-label="Cart"
          style={c.icon ? { color: c.icon } : undefined}
          className="relative text-ink-soft hover:text-ink transition-colors">
          <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <path d="M6 6h15l-1.5 9h-12z"/><path d="M6 6L4.5 3H2"/>
            <circle cx="9.5" cy="20" r="1.2" fill="currentColor" stroke="none"/>
            <circle cx="17.5" cy="20" r="1.2" fill="currentColor" stroke="none"/>
          </svg>
          <CartCountBadge />
        </button>
      )}
      {/* Mobile burger */}
      <button type="button" onClick={() => setMobileOpen(v => !v)} aria-label="Menu"
        style={c.icon ? { color: c.icon } : undefined}
        className="lg:hidden text-ink-soft hover:text-ink transition-colors">
        {mobileOpen
          ? <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M18 6 6 18M6 6l12 12"/></svg>
          : <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M3 12h18M3 6h18M3 18h18"/></svg>
        }
      </button>
    </div>
  );

  const renderLayout = () => {
    switch (layout) {
      case 'centered':
        return (
          <Container className="py-4 flex flex-col items-center gap-3">
            <div className="flex w-full items-center justify-between">
              <div className="flex-1 hidden lg:block" />
              {Logo}
              <div className="flex-1 flex justify-end">
                {Actions}
              </div>
            </div>
            <div className="hidden lg:flex items-center gap-7">
              {navItems.map(item => <NavItem key={item.id} item={item} colors={c} />)}
            </div>
          </Container>
        );
      case 'split':
        return (
          <Container className="flex items-center justify-between py-4">
            <nav className="hidden items-center gap-7 lg:flex">
              {navItems.slice(0, Math.ceil(navItems.length / 2)).map(item => <NavItem key={item.id} item={item} colors={c} />)}
            </nav>
            {Logo}
            <div className="flex items-center gap-7">
              <nav className="hidden lg:flex items-center gap-7">
                {navItems.slice(Math.ceil(navItems.length / 2)).map(item => <NavItem key={item.id} item={item} colors={c} />)}
              </nav>
              {Actions}
            </div>
          </Container>
        );
      case 'minimal':
        return (
          <Container className="flex items-center justify-between py-4">
            {Logo}
            {Actions}
          </Container>
        );
      case 'modern':
        return (
          <Container className="flex items-center justify-between py-4">
            {Logo}
            {NavLinks}
            {Actions}
          </Container>
        );
      default: // classic
        return (
          <Container className="flex items-center justify-between py-4">
            {Logo}
            {NavLinks}
            {Actions}
          </Container>
        );
    }
  };

  return (
    <>
      <header
        style={{ ...bgStyle, ...textStyle, ...borderStyle }}
        className={`${borderCls} ${baseCls} relative`}
      >
        {renderLayout()}

        {/* Mobile drawer */}
        {mobileOpen && (
          <div
            style={{ ...bgStyle, ...textStyle, borderColor: c.border || undefined }}
            className={`lg:hidden border-t ${c.border ? '' : 'border-stone-light'} ${baseCls}`}
          >
            <Container className="py-3">
              {shopAllCfg?.enabled && shopAllCfg?.show_in_mobile_menu && (
                <Link
                  to={shopSlug}
                  onClick={() => setMobileOpen(false)}
                  className="mb-2 flex items-center justify-center gap-2 rounded bg-ink px-4 py-3 text-[12px] font-semibold uppercase tracking-[0.12em] text-cream transition-colors hover:bg-ink/80"
                >
                  {shopLabel}
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                </Link>
              )}
              {navItems.map(item => (
                <div key={item.id}>
                  <Link
                    to={item.url}
                    onClick={() => setMobileOpen(false)}
                    style={{
                      color: c.link || c.text || undefined,
                      borderColor: c.border || undefined,
                    }}
                    className="flex items-center justify-between py-4 text-[13px] font-medium uppercase tracking-[0.1em] text-ink-soft border-b border-stone-light/50 transition-opacity active:opacity-60"
                  >
                    <span>{item.label}</span>
                    {item.children?.length > 0 && (
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="opacity-50"><path d="M6 9l6 6 6-6"/></svg>
                    )}
                  </Link>
                  {item.children?.length > 0 && (
                    <div className="pl-4">
                      {item.children.map(child => (
                        <Link
                          key={child.id}
                          to={child.url}
                          onClick={() => setMobileOpen(false)}
                          style={{
                            color: c.link || c.text || undefined,
                            borderColor: c.border || undefined,
                          }}
                          className="block py-3 text-[12px] text-ink/60 border-b border-stone-light/30 transition-opacity active:opacity-40"
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </Container>
          </div>
        )}
      </header>
    </>
  );
}

// ─── Nav item with dropdown ────────────────────────────────────────────────────

function NavItem({ item, colors }: { item: MenuItem; colors: HeaderSettings['colors'] }) {
  const linkStyle = colors.link ? { color: colors.link } : undefined;
  const baseCls = 'text-[13px] font-medium uppercase tracking-[0.1em] text-ink-soft transition-colors hover:text-ink';
  const hasChildren = item.children?.length > 0;

  if (!hasChildren) {
    return <Link to={item.url} style={linkStyle} className={baseCls}>{item.label}</Link>;
  }

  return (
    <div className="relative group">
      <Link to={item.url} style={linkStyle} className={`${baseCls} flex items-center gap-1`}>
        {item.label}
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M6 9l6 6 6-6"/></svg>
      </Link>
      <div className="absolute left-0 top-full z-50 min-w-[180px] hidden group-hover:block pt-2">
        <div className="rounded-xl border border-stone-light bg-cream shadow-xl py-2">
          {item.children.map((child: MenuItem) => (
            <div key={child.id} className="relative group/sub">
              <Link to={child.url}
                className="flex items-center justify-between px-4 py-2.5 text-[13px] text-ink-soft hover:text-ink hover:bg-stone-light/30 transition-colors">
                {child.label}
                {child.children?.length > 0 && (
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M9 6l6 6-6 6"/></svg>
                )}
              </Link>
              {child.children?.length > 0 && (
                <div className="absolute left-full top-0 z-50 min-w-[160px] hidden group-hover/sub:block">
                  <div className="ml-1 rounded-xl border border-stone-light bg-cream shadow-xl py-2">
                    {child.children.map((grand: MenuItem) => (
                      <Link key={grand.id} to={grand.url}
                        className="block px-4 py-2.5 text-[13px] text-ink-soft hover:text-ink hover:bg-stone-light/30 transition-colors">
                        {grand.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Cart Drawer ────────────────────────────────────────────────────────────────

function CartCountBadge() {
  const { data: cart } = useCart();
  const itemCount = cart?.items?.reduce((sum, item) => sum + item.quantity, 0) ?? 0;
  const bundleCount = cart?.bundles?.reduce((sum, b) => sum + b.quantity, 0) ?? 0;
  const count = itemCount + bundleCount;
  if (count === 0) return null;
  return (
    <span className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-clay text-[9px] font-bold text-cream">
      {count > 9 ? '9+' : count}
    </span>
  );
}

const FREE_SHIPPING_THRESHOLD = 500;

function CartDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { data: cart, isLoading } = useCart();
  const { format } = useCurrency();
  const updateItem = useUpdateCartItem();
  const removeItem = useRemoveCartItem();
  const updateBundle = useUpdateCartBundle();
  const removeBundle = useRemoveCartBundle();

  const items = cart?.items ?? [];
  const bundles = cart?.bundles ?? [];
  const subtotal = cart?.subtotal ?? 0;
  const bundleDiscount = bundles.reduce((sum, b) => sum + Number(b.discount_amount) * b.quantity, 0);
  const displaySubtotal = subtotal + bundleDiscount;
  const total = cart?.total ?? 0;
  const couponDiscount = cart?.coupon_discount ?? 0;
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0)
                   + bundles.reduce((sum, b) => sum + b.quantity, 0);
  const isEmpty = items.length === 0 && bundles.length === 0;

  const remaining = Math.max(0, FREE_SHIPPING_THRESHOLD - subtotal);
  const progress = Math.min(100, (subtotal / FREE_SHIPPING_THRESHOLD) * 100);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Lock body scroll when open
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        className={`fixed inset-0 z-40 bg-ink/30 backdrop-blur-sm transition-opacity duration-300 ${open ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
      />

      {/* Drawer */}
      <div
        className={`fixed right-0 top-0 z-50 flex h-full w-full max-w-sm flex-col bg-cream shadow-2xl transition-transform duration-300 ease-in-out ${open ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-stone-light px-5 py-4">
          <div className="flex items-center gap-2">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path d="M6 6h15l-1.5 9h-12z"/><path d="M6 6L4.5 3H2"/>
              <circle cx="9.5" cy="20" r="1.2" fill="currentColor" stroke="none"/>
              <circle cx="17.5" cy="20" r="1.2" fill="currentColor" stroke="none"/>
            </svg>
            <h2 className="font-serif text-base font-medium text-ink">
              Your Cart {totalItems > 0 && <span className="text-stone">({totalItems})</span>}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full text-stone transition-colors hover:bg-stone-light/40 hover:text-ink"
            aria-label="Close cart"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6 6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>

        {/* Free shipping bar */}
        {subtotal > 0 && (
          <div className="border-b border-stone-light px-5 py-3">
            {remaining > 0 ? (
              <p className="text-[11px] text-stone mb-2">
                Only <span className="font-semibold text-ink">{format(remaining)}</span> away from free shipping
              </p>
            ) : (
              <p className="text-[11px] font-semibold text-moss mb-2">✓ You've unlocked free shipping!</p>
            )}
            <div className="h-1.5 rounded-full bg-stone-light/60 overflow-hidden">
              <div
                className="h-full rounded-full bg-moss transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {isLoading && (
            <div className="space-y-4">
              {[1, 2].map((i) => (
                <div key={i} className="flex gap-3 animate-pulse">
                  <div className="h-16 w-16 shrink-0 rounded-lg bg-stone-light/40" />
                  <div className="flex-1 space-y-2 py-1">
                    <div className="h-3 w-3/4 rounded bg-stone-light/50" />
                    <div className="h-3 w-1/2 rounded bg-stone-light/30" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {!isLoading && isEmpty && (
            <div className="flex flex-col items-center py-16 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full border border-stone-light bg-paper">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-stone">
                  <path d="M6 6h15l-1.5 9h-12z"/><path d="M6 6L4.5 3H2"/>
                  <circle cx="9.5" cy="20" r="1.2" fill="currentColor" stroke="none"/>
                  <circle cx="17.5" cy="20" r="1.2" fill="currentColor" stroke="none"/>
                </svg>
              </div>
              <p className="mt-4 font-serif text-lg font-light text-ink">Your cart is empty</p>
              <button
                type="button"
                onClick={onClose}
                className="mt-4 text-sm text-stone underline decoration-stone-light underline-offset-4 hover:text-ink"
              >
                Continue shopping
              </button>
            </div>
          )}

          <div className="space-y-4">
            {/* Regular cart items */}
            {items.map((item) => {
              const _p = item.product?.images?.[0]?.path;
              const imgSrc = _p ? (_p.startsWith('http') ? _p : `${API_URL}${_p}`) : null;
              const name = item.product?.name ?? `Product #${item.product_id}`;
              const lineTotal = Number(item.unit_price) * item.quantity;
              return (
                <div key={item.id} className="flex gap-3">
                  <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg">
                    {imgSrc
                      ? <img src={imgSrc} alt={name} className="h-full w-full object-cover" />
                      : <EditorialImage seed={`mc-${item.id}`} alt={name} duotone={false} size="thumb" aspect="aspect-square" className="h-full w-full" />
                    }
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-ink line-clamp-2 leading-snug">{name}</p>
                    <p className="mt-0.5 text-xs text-stone">{format(item.unit_price)}</p>
                    {(item.size_label || item.frame_name || item.material_name) && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {item.size_label && <span className="rounded-full bg-parchment px-2 py-0.5 text-[10px] text-stone">{item.size_label}</span>}
                        {item.frame_name && <span className="rounded-full bg-parchment px-2 py-0.5 text-[10px] text-stone">{item.frame_name}</span>}
                        {item.material_name && <span className="rounded-full bg-parchment px-2 py-0.5 text-[10px] text-stone">{item.material_name}</span>}
                      </div>
                    )}
                    <div className="mt-2 flex items-center justify-between">
                      <div className="flex items-center gap-1 rounded-full border border-stone-light">
                        <button type="button" onClick={() => { if (item.quantity > 1) updateItem.mutate({ id: item.id, quantity: item.quantity - 1 }); }} disabled={item.quantity <= 1 || updateItem.isPending} className="flex h-6 w-6 items-center justify-center rounded-full text-stone hover:text-ink disabled:opacity-30">
                          <svg width="10" height="2" viewBox="0 0 10 2" fill="currentColor"><rect width="10" height="2" rx="1"/></svg>
                        </button>
                        <span className="w-5 text-center text-xs font-medium text-ink">{item.quantity}</span>
                        <button type="button" onClick={() => updateItem.mutate({ id: item.id, quantity: item.quantity + 1 })} disabled={updateItem.isPending} className="flex h-6 w-6 items-center justify-center rounded-full text-stone hover:text-ink disabled:opacity-30">
                          <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor"><rect x="4" width="2" height="10" rx="1"/><rect y="4" width="10" height="2" rx="1"/></svg>
                        </button>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-semibold text-ink tabular-nums">{format(lineTotal)}</span>
                        <button type="button" onClick={() => removeItem.mutate(item.id)} className="text-[11px] text-stone/60 hover:text-clay transition-colors">Remove</button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Bundle items */}
            {bundles.map((bundle) => {
              const rawImg = bundle.bundle_image;
              const bundleImg = rawImg ? (rawImg.startsWith('http') ? rawImg : `${API_URL}/storage/${rawImg.replace(/^\/?storage\//, '')}`) : null;
              const lineTotal = bundle.unit_price * bundle.quantity;
              return (
                <div key={`b-${bundle.id}`} className="rounded-xl border border-[#C9A84C]/30 bg-[#C9A84C]/5 overflow-hidden">
                  {/* Bundle header */}
                  <div className="flex items-center gap-3 px-3 pt-3 pb-2">
                    <div className="h-12 w-12 shrink-0 overflow-hidden rounded-lg border border-[#C9A84C]/20">
                      {bundleImg
                        ? <img src={bundleImg} alt={bundle.bundle_name} className="h-full w-full object-cover" />
                        : <div className="flex h-full w-full items-center justify-center bg-[#C9A84C]/10 text-lg">🎁</div>
                      }
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[9px] font-bold uppercase tracking-wider text-[#C9A84C]">Bundle</span>
                      </div>
                      <p className="text-sm font-semibold text-ink leading-snug line-clamp-1">{bundle.bundle_name}</p>
                      <p className="text-[10px] text-stone">{bundle.products.length} products included</p>
                    </div>
                  </div>

                  {/* Product list */}
                  <div className="mx-3 mb-2 rounded-lg bg-white/60 divide-y divide-stone-light/40">
                    {bundle.products.map((p, i) => {
                      const pImg = p.product_image ? (p.product_image.startsWith('http') ? p.product_image : `${API_URL}/storage/${p.product_image.replace(/^\/?storage\//, '')}`) : null;
                      const opts = [p.size_label, p.frame_name, p.material_name].filter(Boolean).join(' · ');
                      return (
                        <div key={i} className="flex items-center gap-2 px-2 py-1.5">
                          <div className="h-8 w-8 shrink-0 overflow-hidden rounded-md">
                            {pImg ? <img src={pImg} alt={p.product_name} className="h-full w-full object-cover" /> : <div className="h-full w-full bg-stone-light/30 rounded-md" />}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-[11px] font-medium text-ink line-clamp-1">{p.product_name}</p>
                            {opts && <p className="text-[10px] text-stone">{opts}</p>}
                          </div>
                          <span className="text-[11px] text-stone tabular-nums shrink-0">{format(p.line_total)}</span>
                        </div>
                      );
                    })}
                  </div>

                  {/* Pricing + controls */}
                  <div className="flex items-center justify-between px-3 pb-3">
                    <div className="flex items-center gap-1 rounded-full border border-stone-light bg-white/60">
                      <button type="button" onClick={() => { if (bundle.quantity > 1) updateBundle.mutate({ id: bundle.id, quantity: bundle.quantity - 1 }); }} disabled={bundle.quantity <= 1 || updateBundle.isPending} className="flex h-6 w-6 items-center justify-center rounded-full text-stone hover:text-ink disabled:opacity-30">
                        <svg width="10" height="2" viewBox="0 0 10 2" fill="currentColor"><rect width="10" height="2" rx="1"/></svg>
                      </button>
                      <span className="w-5 text-center text-xs font-medium text-ink">{bundle.quantity}</span>
                      <button type="button" onClick={() => updateBundle.mutate({ id: bundle.id, quantity: bundle.quantity + 1 })} disabled={updateBundle.isPending} className="flex h-6 w-6 items-center justify-center rounded-full text-stone hover:text-ink disabled:opacity-30">
                        <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor"><rect x="4" width="2" height="10" rx="1"/><rect y="4" width="10" height="2" rx="1"/></svg>
                      </button>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        {bundle.discount_amount > 0 && (
                          <p className="text-[10px] text-stone line-through tabular-nums">{format(bundle.original_price * bundle.quantity)}</p>
                        )}
                        <p className="text-xs font-bold text-ink tabular-nums">{format(lineTotal)}</p>
                        {bundle.discount_amount > 0 && (
                          <p className="text-[9px] font-semibold text-[#C9A84C]">Save {bundle.discount_percent}%</p>
                        )}
                      </div>
                      <button type="button" onClick={() => removeBundle.mutate(bundle.id)} className="text-[11px] text-stone/60 hover:text-clay transition-colors">Remove</button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        {!isEmpty && (
          <div className="border-t border-stone-light px-5 py-5 space-y-4">
            <div className="space-y-1.5">
              <div className="flex justify-between text-sm text-ink-soft">
                <span>Subtotal</span>
                <span className="tabular-nums">{format(displaySubtotal)}</span>
              </div>
              {bundleDiscount > 0 && (
                <div className="flex justify-between text-sm text-moss">
                  <span>Bundle discount</span>
                  <span className="tabular-nums">−{format(bundleDiscount)}</span>
                </div>
              )}
              {couponDiscount > 0 && (
                <div className="flex justify-between text-sm text-moss">
                  <span>Discount</span>
                  <span className="tabular-nums">−{format(couponDiscount)}</span>
                </div>
              )}
              <div className="flex justify-between text-base font-semibold text-ink">
                <span>Total</span>
                <span className="tabular-nums">{format(total)}</span>
              </div>
              <p className="text-[11px] text-stone">Shipping calculated at checkout</p>
            </div>

            <Link
              to="/checkout"
              onClick={onClose}
              className="block w-full rounded-full bg-ink py-3 text-center text-[12px] font-semibold uppercase tracking-widest text-cream transition-all hover:bg-clay hover:shadow-md active:scale-[0.98]"
            >
              Checkout · {format(total)}
            </Link>
            <Link
              to="/cart"
              onClick={onClose}
              className="block w-full rounded-full border border-stone-light py-3 text-center text-[12px] font-medium text-ink-soft transition-colors hover:border-ink hover:text-ink"
            >
              View full cart
            </Link>
          </div>
        )}
      </div>
    </>
  );
}

// ─── Footer ────────────────────────────────────────────────────────────────────

function Footer({ ftr, brand, settings }: { ftr: FooterSettings; brand: BrandSettings; settings: ReturnType<typeof useSettings>['data'] }) {
  const { data: socialLinks } = useSocialLinks();
  const { data: paymentMethods } = usePaymentMethods();
  const { data: categories } = usePublicCategories();

  const siteName = brand.identity.brand_name || settings?.site_name || 'Framed Studio';
  const fallbackLogo = settings?.logo ?? null;
  const socials = socialLinks ?? [];
  const payments = paymentMethods ?? [];
  const c = ftr.colors;
  const footerLogoCfg = ftr.logo_component ?? DEFAULT_LOGO_COMPONENT;

  // Resolve footer logo from brand
  const footerLogoUrl = resolveLogoUrl(brand.logos, footerLogoCfg, fallbackLogo, 'default');
  const footerLogoWidth = resolveLogoWidth(footerLogoCfg, 'default') ?? footerLogoCfg.max_width ?? 160;

  const bg = c.bg || '#1a1a1a';
  const text = c.text || '#f5f0e8';
  const copyrightBg = c.copyright_bg || (c.bg ? undefined : '#111');
  const copyrightText = c.copyright_text || c.secondary_text || undefined;
  const borderColor = c.border || 'rgba(245,240,232,0.1)';
  const mutedColor = c.secondary_text || 'rgba(245,240,232,0.6)';
  const linkColor = c.link || mutedColor;
  const linkHoverColor = c.link_hover || text;
  const headingColor = c.heading || text;
  const iconColor = c.icon || mutedColor;
  const iconHoverColor = c.icon_hover || text;
  const copyrightSuffix = brand.identity.copyright_name ? `${brand.identity.copyright_name}. ${ftr.copyright.suffix}` : ftr.copyright.suffix;

  const sharedCtx = {
    siteName, footerLogoUrl, footerLogoWidth, footerLogoCfg, brand,
    socials, payments, categories: categories ?? [],
    ftr,
    colors: { text, mutedColor, linkColor, linkHoverColor, headingColor, borderColor, iconColor, iconHoverColor },
  };

  return (
    <footer style={{ background: bg, color: text }}>
      {/* Trust bar */}
      {ftr.trust_bar.enabled && ftr.trust_bar.items.length > 0 && (
        <div style={{ borderBottomColor: borderColor }} className="border-b">
          <Container className="grid grid-cols-2 gap-6 py-8 text-center sm:grid-cols-4">
            {ftr.trust_bar.items.map((item, i) => (
              <div key={i}>
                <TrustIcon name={item.icon} cls="mx-auto mb-3 h-6 w-6" />
                <p className="text-xs font-semibold uppercase tracking-[0.08em]" style={{ color: headingColor }}>{item.title}</p>
                <p className="mt-1 text-xs" style={{ color: mutedColor }}>{item.subtitle}</p>
              </div>
            ))}
          </Container>
        </div>
      )}

      {/* Column grid */}
      <style>{`
        .footer-col-grid { grid-template-columns: 1fr; }
        @media (min-width: 640px)  { .footer-col-grid { grid-template-columns: repeat(2, 1fr); } }
        @media (min-width: 1024px) { .footer-col-grid { grid-template-columns: ${ftr.columns.map(c => `${c.width}fr`).join(' ')}; } }
      `}</style>
      <Container className="py-10 lg:py-16">
        <div className="footer-col-grid grid gap-x-8 gap-y-10">
          {ftr.columns.map((col, i) => (
            <div key={i} className="min-w-0">
              {col.widgets.map((widget, j) => (
                <div key={j} className="mb-6 last:mb-0">
                  <WidgetRenderer widget={widget} ctx={sharedCtx} />
                </div>
              ))}
            </div>
          ))}
        </div>
      </Container>

      {/* Bottom bar */}
      <div style={{ background: copyrightBg || bg, borderTopColor: borderColor, color: copyrightText || mutedColor }} className="border-t">
        <Container className="flex flex-col items-center justify-between gap-4 py-6 text-xs md:flex-row">
          <p>© {new Date().getFullYear()} {siteName}. {copyrightSuffix}</p>
          <div className="flex items-center gap-6">
            {socials.length > 0
              ? socials.map(s => (
                  <a key={s.id} href={s.url} target="_blank" rel="noopener noreferrer"
                    style={{ color: linkColor }} className="capitalize transition-colors"
                    onMouseEnter={e => (e.currentTarget.style.color = linkHoverColor)}
                    onMouseLeave={e => (e.currentTarget.style.color = linkColor)}>
                    {s.platform}
                  </a>
                ))
              : <><span>Instagram</span><span>Pinterest</span></>
            }
            <Link to="/faqs" style={{ color: linkColor }}
              onMouseEnter={e => (e.currentTarget.style.color = linkHoverColor)}
              onMouseLeave={e => (e.currentTarget.style.color = linkColor)}>FAQs</Link>
            <Link to="/contact" style={{ color: linkColor }}
              onMouseEnter={e => (e.currentTarget.style.color = linkHoverColor)}
              onMouseLeave={e => (e.currentTarget.style.color = linkColor)}>Contact</Link>
          </div>
        </Container>
      </div>
    </footer>
  );
}

// ─── Widget renderer ───────────────────────────────────────────────────────────

interface WidgetCtx {
  siteName: string;
  footerLogoUrl: string | null; footerLogoWidth: number; footerLogoCfg: LogoComponent;
  brand: BrandSettings;
  socials: Array<{ id: number; platform: string; url: string; icon: string | null }>;
  payments: Array<{ id: number; name: string; code: string }>;
  categories: Array<{ id: number; name: string; slug: string; parent_id: number | null }>;
  ftr: FooterSettings;
  colors: { text: string; mutedColor: string; linkColor: string; linkHoverColor: string; headingColor: string; borderColor: string; iconColor: string; iconHoverColor: string };
}

function WidgetRenderer({ widget, ctx }: { widget: FooterWidget; ctx: WidgetCtx }) {
  const { colors, ftr, footerLogoUrl, footerLogoWidth, footerLogoCfg, siteName, brand } = ctx;
  const { text, mutedColor, linkColor, linkHoverColor, headingColor } = colors;

  switch (widget.type) {
    case 'logo':
      if (footerLogoUrl) {
        return (
          <div>
            <img src={`${API_URL}${footerLogoUrl}`} alt={siteName}
              style={{ maxWidth: footerLogoWidth, objectFit: footerLogoCfg.object_fit, borderRadius: footerLogoCfg.border_radius || undefined }}
              className="h-auto w-auto object-contain" />
            {footerLogoCfg.show_brand_name && (
              <p className="mt-2 font-serif text-xl tracking-tight" style={{ color: text }}>{siteName}</p>
            )}
            {footerLogoCfg.show_tagline && brand.identity.tagline && (
              <p className="mt-1 text-xs tracking-widest opacity-60" style={{ color: mutedColor }}>{brand.identity.tagline}</p>
            )}
          </div>
        );
      }
      return (
        <div>
          <p className="font-serif text-3xl tracking-tight" style={{ color: text }}>{siteName.replace(/\.$/, '')}<span className="text-clay-light">.</span></p>
          {footerLogoCfg.show_tagline && brand.identity.tagline && (
            <p className="mt-1 text-xs tracking-widest opacity-60" style={{ color: mutedColor }}>{brand.identity.tagline}</p>
          )}
        </div>
      );

    case 'text':
      return <p className="text-sm leading-relaxed" style={{ color: mutedColor }}>{widget.content}</p>;

    case 'nav_links':
      return (
        <div>
          {widget.title && <p className="text-[11px] font-medium uppercase tracking-[0.2em] mb-4" style={{ color: headingColor }}>{widget.title}</p>}
          <ul className="space-y-3">
            {(widget.links ?? []).map((link, i) => (
              <li key={i}>
                <Link to={link.url} style={{ color: linkColor }} className="text-sm transition-colors"
                  onMouseEnter={e => (e.currentTarget.style.color = linkHoverColor)}
                  onMouseLeave={e => (e.currentTarget.style.color = linkColor)}>
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      );

    case 'categories': {
      const rootCats = ctx.categories.filter(c => c.parent_id === null).slice(0, widget.limit ?? 8);
      return (
        <div>
          {widget.title && <p className="text-[11px] font-medium uppercase tracking-[0.2em] mb-4" style={{ color: headingColor }}>{widget.title}</p>}
          <ul className="space-y-3">
            {rootCats.map(cat => (
              <li key={cat.id}>
                <Link to={`/categories/${cat.slug}`} style={{ color: linkColor }} className="text-sm transition-colors"
                  onMouseEnter={e => (e.currentTarget.style.color = linkHoverColor)}
                  onMouseLeave={e => (e.currentTarget.style.color = linkColor)}>
                  {cat.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      );
    }

    case 'newsletter':
      return (
        <div style={{ background: ftr.colors.newsletter_bg || 'transparent' }} className={ftr.colors.newsletter_bg ? 'p-5 rounded-xl' : ''}>
          {widget.eyebrow && <p className="text-[11px] font-medium uppercase tracking-[0.2em] mb-2" style={{ color: mutedColor }}>{widget.eyebrow}</p>}
          {widget.heading && <p className="text-sm leading-relaxed mb-5" style={{ color: mutedColor }}>{widget.heading}</p>}
          <form className="flex items-center border-b pb-2" style={{ borderColor: colors.borderColor }} onSubmit={e => e.preventDefault()}>
            <input type="email" required placeholder={widget.placeholder ?? 'Your email'}
              className="w-full bg-transparent text-sm focus:outline-none"
              style={{ color: text }} />
            <button type="submit" className="text-sm font-medium transition-colors shrink-0 ml-2"
              style={ftr.colors.newsletter_button ? { color: ftr.colors.newsletter_button } : { color: mutedColor }}>
              {widget.button_text ?? 'Join →'}
            </button>
          </form>
        </div>
      );

    case 'social_icons': {
      const size = ftr.social_icons.size;
      const shape = ftr.social_icons.shape;
      const shapeClass = shape === 'circle' ? 'rounded-full p-2 border' : shape === 'square' ? 'p-2 border' : shape === 'rounded' ? 'rounded-lg p-2 border' : '';
      return (
        <div className="flex flex-wrap gap-3 mt-1">
          {ctx.socials.length > 0
            ? ctx.socials.map(s => (
                <a key={s.id} href={s.url} target="_blank" rel="noopener noreferrer" aria-label={s.platform}
                  style={{ color: colors.iconColor, borderColor: colors.borderColor }}
                  className={`transition-colors ${shapeClass}`}
                  onMouseEnter={e => (e.currentTarget.style.color = colors.iconHoverColor)}
                  onMouseLeave={e => (e.currentTarget.style.color = colors.iconColor)}>
                  <SocialIconSvg platform={s.platform} size={size} />
                </a>
              ))
            : (['instagram', 'pinterest', 'facebook'] as const).map(p => (
                <a key={p} href="#" aria-label={p}
                  style={{ color: colors.iconColor, borderColor: colors.borderColor }}
                  className={`transition-colors ${shapeClass}`}
                  onMouseEnter={e => (e.currentTarget.style.color = colors.iconHoverColor)}
                  onMouseLeave={e => (e.currentTarget.style.color = colors.iconColor)}>
                  <SocialIconSvg platform={p} size={size} />
                </a>
              ))
          }
        </div>
      );
    }

    case 'payment_icons':
      return (
        <div className="flex flex-wrap items-center gap-3 text-[11px] uppercase tracking-[0.1em]" style={{ color: mutedColor }}>
          {ctx.payments.length > 0
            ? ctx.payments.map((pm, i) => <span key={pm.id}>{pm.name}{i < ctx.payments.length - 1 && <span className="ml-3">·</span>}</span>)
            : <><span>Visa</span><span>·</span><span>Mastercard</span><span>·</span><span>PayPal</span></>
          }
        </div>
      );

    case 'contact':
      return (
        <div className="space-y-2 text-sm" style={{ color: mutedColor }}>
          {widget.address && <p>{widget.address}</p>}
          {widget.phone && <p><a href={`tel:${widget.phone}`} style={{ color: linkColor }}
            onMouseEnter={e => (e.currentTarget.style.color = linkHoverColor)}
            onMouseLeave={e => (e.currentTarget.style.color = linkColor)}>{widget.phone}</a></p>}
          {widget.email && <p><a href={`mailto:${widget.email}`} style={{ color: linkColor }}
            onMouseEnter={e => (e.currentTarget.style.color = linkHoverColor)}
            onMouseLeave={e => (e.currentTarget.style.color = linkColor)}>{widget.email}</a></p>}
        </div>
      );

    case 'html':
      return <div className="text-sm" style={{ color: mutedColor }} dangerouslySetInnerHTML={{ __html: widget.content ?? '' }} />;

    default:
      return null;
  }
}
